// ═══════════════════════════════════════════════════════════════
// inspection-helpers — progress math, legacy migration, and the
// customer-facing report snapshot.
// ═══════════════════════════════════════════════════════════════
import {
  TIRE_CORNERS, TIRE_SUGGESTIONS,
  treadStatus, blankTireGrid, cloneDefaultChecklist, toMeasurement,
} from './inspection-template.js';

// `short` is for space-constrained spots (the tire table on a phone),
// where the colour plus the legend above already carry the meaning
// and the full sentence would push the column off screen.
export const EXTRA_SECTION_LABEL = 'Additional recommendations';

export function statusMeta(status) {
  if (status === 'good')      return { label: 'Checked and OK', short: 'OK', cls: 'insp-good' };
  if (status === 'attention') return { label: 'May require attention', short: 'Attention', cls: 'insp-attention' };
  if (status === 'replace')   return { label: 'Requires immediate attention', short: 'Immediate', cls: 'insp-replace' };
  return { label: 'Not checked', short: '—', cls: 'insp-none' };
}


// ─── Legacy migration ───────────────────────────────────────────
// Inspections saved before templates existed are a flat
// `items: [{ key, label, status, note, photos }]` array with no
// sections. Wrap them in a single section so the wizard, the
// progress math, and the report can all walk one shape.
//
// Pure — never mutates the doc it was handed.
export function normalizeInspection(raw) {
  if (!raw) return null;
  if (Array.isArray(raw.sections)) {
    // extraItems arrived after sections did; older docs lack it.
    return Array.isArray(raw.extraItems) ? raw : { ...raw, extraItems: [] };
  }
  if (Array.isArray(raw.items)) {
    return {
      ...raw,
      templateId: raw.templateId || 'legacy',
      templateName: raw.templateName || 'Vehicle inspection',
      sections: [{
        key: 'legacy',
        label: 'Vehicle inspection',
        kind: 'status',
        items: raw.items.map((i) => ({
          key: i.key,
          label: i.label || '',
          status: i.status ?? null,
          note: i.note || '',
          photos: Array.isArray(i.photos) ? i.photos : [],
        })),
      }],
      extraItems: [],
      odometer: raw.odometer ?? '',
      comments: raw.comments || '',
    };
  }
  return raw;
}

// ─── Progress ───────────────────────────────────────────────────
// A tire section counts as answered once every corner has a tread
// reading — that's the number the whole grid hangs off.
function sectionCounts(section) {
  if (section?.kind === 'tires') {
    const corners = section.tires?.corners || {};
    const answered = TIRE_CORNERS.filter(
      ({ key }) => toMeasurement(corners[key]?.tread) !== null
    ).length;
    return { checked: answered, total: TIRE_CORNERS.length };
  }
  const items = Array.isArray(section?.items) ? section.items : [];
  return { checked: items.filter((i) => i?.status).length, total: items.length };
}

export function sectionProgress(section) {
  return sectionCounts(section);
}

export function inspectionProgress(inspection) {
  const insp = normalizeInspection(inspection);
  const sections = Array.isArray(insp?.sections) ? insp.sections : [];
  return sections.reduce(
    (acc, s) => {
      const { checked, total } = sectionCounts(s);
      return { checked: acc.checked + checked, total: acc.total + total };
    },
    { checked: 0, total: 0 }
  );
}

export function inspectionIsComplete(inspection) {
  const { checked, total } = inspectionProgress(inspection);
  return total > 0 && checked === total;
}

// Everything the tech flagged yellow or red — the raw material for
// the recommended-repairs list the customer will accept or decline.
export function inspectionFindings(inspection) {
  const insp = normalizeInspection(inspection);
  const out = [];
  (insp?.sections || []).forEach((section) => {
    if (section.kind === 'tires') {
      const corners = section.tires?.corners || {};
      TIRE_CORNERS.forEach(({ key, label }) => {
        const c = corners[key] || {};
        const status = c.wear || treadStatus(c.tread);
        if (status === 'attention' || status === 'replace') {
          const measured = toMeasurement(c.tread);
          const tread = measured === null ? '' : ` — ${measured}/32"`;
          out.push({
            key: `tire-${key}`,
            sectionLabel: section.label,
            label: `${label} tire${tread}`,
            status,
            note: section.tires?.note || '',
            photos: section.tires?.photos || [],
          });
        }
      });
      return;
    }
    (section.items || []).forEach((item) => {
      if (item.status === 'attention' || item.status === 'replace') {
        out.push({
          key: item.key,
          sectionLabel: section.label,
          label: item.label,
          status: item.status,
          note: item.note || '',
          photos: item.photos || [],
        });
      }
    });
  });

  // Anything the tech added by hand at the end of the wizard is a
  // finding like any other — that's the point of adding it.
  (insp?.extraItems || []).forEach((item) => {
    const label = (item.label || '').trim();
    if (!label) return;
    if (item.status !== 'attention' && item.status !== 'replace') return;
    out.push({
      key: item.key,
      sectionLabel: EXTRA_SECTION_LABEL,
      label,
      status: item.status,
      note: item.note || '',
      photos: item.photos || [],
    });
  });

  return out;
}

// ─── Customer-facing snapshot ───────────────────────────────────
// Frozen onto sharedEstimates / sharedInvoices at send time so a
// later edit to the inspection can't rewrite what the customer saw.
// URLs only — never Storage paths, which are useless to a customer
// and leak the internal layout.
//
// Unanswered items are dropped: a half-finished checklist should
// show what WAS checked, not a wall of "Not checked".
export function buildInspectionSnapshot(inspection) {
  const insp = normalizeInspection(inspection);
  if (!insp) return null;

  const sections = [];
  (insp.sections || []).forEach((section) => {
    if (section.kind === 'tires') {
      const grid = section.tires || blankTireGrid();
      const corners = TIRE_CORNERS
        .map(({ key, label, short }) => {
          const c = grid.corners?.[key] || {};
          const tread = toMeasurement(c.tread);
          const status = c.wear || treadStatus(c.tread);
          if (tread === null && !status) return null;
          return {
            key, label, short,
            tread,
            pressureBefore: toMeasurement(c.pressureBefore),
            pressureAfter: toMeasurement(c.pressureAfter),
            status: status || null,
          };
        })
        .filter(Boolean);

      const suggestions = TIRE_SUGGESTIONS
        .filter(({ key }) => grid.suggestions?.[key])
        .map(({ label }) => label);

      if (corners.length === 0 && suggestions.length === 0) return;
      sections.push({
        key: section.key,
        label: section.label,
        kind: 'tires',
        corners,
        suggestions,
        tpms: grid.tpms || null,
        note: (grid.note || '').trim(),
        photoUrls: (grid.photos || []).map((p) => p.url).filter(Boolean),
      });
      return;
    }

    const items = (section.items || [])
      .filter((i) => i?.status)
      .map((i) => ({
        key: i.key,
        label: i.label,
        status: i.status,
        note: (i.note || '').trim(),
        photoUrls: (i.photos || []).map((p) => p.url).filter(Boolean),
      }));
    if (items.length === 0) return;
    sections.push({ key: section.key, label: section.label, kind: 'status', items });
  });

  // Extras ride along as a final section so the customer sees them in
  // the same colour-coded language as everything else. Unlabelled
  // rows are dropped — a half-typed suggestion isn't a finding.
  const extras = (insp.extraItems || [])
    .filter((i) => (i.label || '').trim() && i.status)
    .map((i) => ({
      key: i.key,
      label: i.label.trim(),
      status: i.status,
      note: (i.note || '').trim(),
      photoUrls: (i.photos || []).map((p) => p.url).filter(Boolean),
    }));
  if (extras.length > 0) {
    sections.push({
      key: '__extra__',
      label: EXTRA_SECTION_LABEL,
      kind: 'status',
      items: extras,
    });
  }

  if (sections.length === 0) return null;

  return {
    templateName: insp.templateName || 'Vehicle inspection',
    completedAt: insp.completedAt || null,
    odometer: toMeasurement(insp.odometer),
    comments: (insp.comments || '').trim(),
    sections,
  };
}

// Does this inspection have anything worth showing a customer?
export function hasReportableInspection(inspection) {
  return buildInspectionSnapshot(inspection) !== null;
}

export { cloneDefaultChecklist };
